import {
  Badge,
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  InlineStack,
  Link,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import plur from 'plur';
import { UsageChips, type UsageChipProps } from '../components/usage-chips';
import type { Conversion } from './adapter';
import { conversionImpact, countedWhen } from './rows';

/**
 * The cells of the Conversions tab.
 *
 * The layout and copy are the spike's (`customerio/ui#16717`,
 * `app/components/data-index-conversions-table`), read from a goal rather than
 * from a definition built in the browser.
 */

export type ConversionNameCellProps = {
  conversion: Conversion;
  /** The goal's own page, which the row opens. */
  href: string;
};

/**
 * A conversion's name, and the two things about it worth knowing at a glance:
 * whether the workspace wrote it or the migration did, and whether it is
 * still counting.
 *
 * The name is a primary link to the goal, styled like the name in the lists
 * beside this one (`components/name-cell.tsx`) rather than like the spike's
 * underline-less secondary link: a table cell has nothing around it to mark a
 * link by, so the link has to look like one on its own.
 *
 * The badges sit outside it. They describe the conversion rather than name
 * it, and inside the link they would become part of what it is called — "Placed
 * an order Migrated Tracking" is what a screen reader would announce.
 */
export function ConversionNameCell({ conversion, href }: ConversionNameCellProps) {
  return (
    <InlineStack gap="100" alignBlock="center">
      <Link href={href} variant="primary" truncate>
        {conversion.name}
      </Link>
      {conversion.isMigrated ? <Badge>Migrated</Badge> : null}
      <TrackingBadge isTracking={conversion.isTracking} />
    </InlineStack>
  );
}

type TrackingBadgeProps = {
  isTracking: boolean;
};

/**
 * Whether the conversion is still counting.
 *
 * Both states are shown rather than only the exceptional one: a column that
 * marks nothing on a tracking row makes an untracked row read as a rendering
 * gap rather than as a state someone chose.
 */
function TrackingBadge({ isTracking }: TrackingBadgeProps) {
  return (
    <Badge showStatusIndicator color={isTracking ? 'success' : 'neutral'}>
      {isTracking ? 'Tracking' : 'Not tracking'}
    </Badge>
  );
}

export type ConversionCountedWhenCellProps = {
  conversion: Conversion;
};

/** When the conversion counts, or that its sources do not agree on one answer. */
export function ConversionCountedWhenCell({ conversion }: ConversionCountedWhenCellProps) {
  const when = countedWhen(conversion);

  if (when.kind === 'varies') {
    return (
      // Underlined because the trigger is inline text: the tooltip carries the
      // whole reason the cell says Varies, and nothing else in the cell says
      // there is more to read.
      <Tooltip content="The things using this conversion count it over different windows." placement="top" isUnderlined>
        <Text color="subtle">Varies</Text>
      </Tooltip>
    );
  }

  return <Text color="subtle">{when.text}</Text>;
}

/**
 * How many people converted recently.
 *
 * The count itself is AUD-4576: it needs a read of its own that the list call
 * does not carry, and one round trip per row is not what this column is for.
 * The column is here so the shape of the table is settled, and says plainly
 * that the figure is not in yet rather than showing a zero.
 */
export function ConversionCountCell() {
  return <Text color="subtle">Not available yet</Text>;
}

export type ConversionImpactCellProps = {
  conversion: Conversion;
  /** The goal's own page, where a value is set. */
  href: string;
};

/** What a conversion is worth, or a way to say what it is worth. */
export function ConversionImpactCell({ conversion, href }: ConversionImpactCellProps) {
  const impact = conversionImpact(conversion);

  if (impact.kind === 'value') {
    return <Text>{impact.text}</Text>;
  }

  if (impact.kind === 'attribute') {
    return (
      // Underlined for the same reason as Varies: the attribute the value is
      // read from is only in the tooltip.
      <Tooltip
        content={
          impact.attribute == null
            ? 'Each conversion is worth the value on the event that triggered it.'
            : `Each conversion is worth the "${impact.attribute}" attribute of the event that triggered it.`
        }
        placement="top"
        isUnderlined
      >
        <Text color="subtle">From event attribute</Text>
      </Tooltip>
    );
  }

  return (
    <Link href={href} variant="primary">
      Add value
    </Link>
  );
}

export type ConversionUsageTerms = {
  /** What the workspace calls a campaign, singular. */
  campaignSingular: string;
  /** What the workspace calls a broadcast, singular. */
  newsletterSingular: string;
};

export type ConversionUsageCellProps = {
  conversion: Conversion;
  terms: ConversionUsageTerms;
};

const LIST = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });

/** A surface a conversion is used by, and what the workspace calls one of them. */
type NamedUsageChip = UsageChipProps & { noun: string };

/**
 * What uses a conversion, counted by kind.
 *
 * Only the three surfaces that carry conversion settings can use one, which
 * is why this counts three things where the attribute and event rows beside
 * it count seven.
 */
export function ConversionUsageCell({ conversion, terms }: ConversionUsageCellProps) {
  const chips = (
    [
      { icon: 'campaigns', count: conversion.usage.campaigns, noun: terms.campaignSingular },
      { icon: 'newsletter', count: conversion.usage.newsletters, noun: terms.newsletterSingular },
      { icon: 'transactional-api', count: conversion.usage.transactional, noun: 'transactional message' },
    ] satisfies NamedUsageChip[]
  ).filter((chip) => chip.count > 0);

  if (chips.length === 0) {
    return <Text color="subtle">-</Text>;
  }

  const tooltip = LIST.format(chips.map((chip) => `${String(chip.count)} ${plur(chip.noun, chip.count)}`));

  return <UsageChips chips={chips} tooltip={tooltip} />;
}

export type ConversionsCountNoteProps = {
  /** How many days the count column covers. */
  days: number;
};

/** What the count column counts, which the column header has no room to say. */
export function ConversionsCountNote({ days }: ConversionsCountNoteProps) {
  return <Text color="subtle">{`People who met the criteria in the last ${String(days)} ${plur('day', days)}.`}</Text>;
}

/**
 * A list that could not be read.
 *
 * Kept apart from the empty state: a workspace with no conversions and a
 * workspace whose conversions failed to load look identical in an empty
 * table, and only one of them should be told to try again.
 */
export function ConversionsLoadError() {
  return (
    <EmptyState>
      <EmptyStateTitle>Conversions could not be loaded</EmptyStateTitle>
      <EmptyStateDescription>Reload the page to try again.</EmptyStateDescription>
    </EmptyState>
  );
}
